import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-backoffice-client/components/badge"
import { DotText } from "@/v2-backoffice-client/components/dot"
import InfoGrid, {
  createInfoGridFields,
  type InfoGridProps,
} from "@/v2-backoffice-client/components/infoGrid"
import {
  CHANGE_BADGE_VARIANT,
  changeWordOf,
  DECISION_STATUS_DOT_VARIANT,
  moduleNameOf,
  releaseOf,
  statusOf,
  whoHasIt,
  type LicenseChange,
} from "@/v2-backoffice-client/license/changes/license.changes.utils"

const FIELDS = createInfoGridFields<LicenseChange>({
  fields: {
    Decision: {
      label: "Decision",
      description:
        "Needs a decision: free for every customer and nobody has said whether that is intended. Free for everyone: kept open on purpose. Package-only: locked to the packages that include it.",
      accessor: (data) => statusOf(data),
      renderValue({ data }) {
        const status = statusOf(data)
        return (
          <DotText variant={DECISION_STATUS_DOT_VARIANT[status]}>
            {status}
          </DotText>
        )
      },
    },
    WhoHasIt: {
      label: "Who has it",
      description:
        "Who can use the capability right now. Everyone, until it is made package-only; then only customers whose license or add-on carries one of the packages that include it.",
      accessor: (data) => whoHasIt(data),
    },
    Module: {
      label: "Part of",
      description: "The module or panel of Fusion the capability belongs to.",
      accessor: (data) => moduleNameOf(data),
    },
    Change: {
      label: "Change",
      description:
        "New: a release added it. Removed: it is gone from the app. Moved: it sits somewhere else in the app now.",
      accessor: (data) => changeWordOf(data),
      renderValue({ data }) {
        return (
          <Badge
            size="xs"
            variant={CHANGE_BADGE_VARIANT[data.ChangeKindCode] ?? "outline"}
          >
            {changeWordOf(data)}
          </Badge>
        )
      },
    },
    ChangedAt: {
      label: "Since",
      description:
        "When a deploy's scan of the app first saw the change; for capabilities that predate the feed, the day their code first appeared.",
      accessor: (data) => formatDateString(data.ChangedAt),
    },
    Release: {
      label: "Released in",
      description:
        "The first release that shipped the capability. “Not released yet” means it is only on a branch so far.",
      accessor: (data) => releaseOf(data),
      valueClassName: "truncate",
    },
    Decided: {
      label: "Kept free by",
      accessor: (data) =>
        data.FreeConfirmedAt
          ? `${data.FreeConfirmedByUserName ?? "an operator"}, ${formatDateString(data.FreeConfirmedAt)}`
          : null,
      show: (data) => Boolean(data.FreeConfirmedAt),
    },
    Code: {
      label: "Code",
      description:
        "The capability's technical name: what the console gates on and what a developer searches the code for.",
      accessor: "SurfaceCode",
      valueClassName: "font-mono text-[11px] truncate",
    },
  },
})

export type LicenseCapabilityViewInfoProps = Omit<
  InfoGridProps<LicenseChange>,
  "data" | "fields"
> & {
  change: LicenseChange | null | undefined
}

function LicenseCapabilityViewInfo(props: LicenseCapabilityViewInfoProps) {
  const { change, className, ...infoGridProps } = props

  return (
    <InfoGrid
      id="license-capability-info"
      className={cn("license-capability-view-info", className)}
      data={change}
      fields={FIELDS}
      {...infoGridProps}
    />
  )
}

export default LicenseCapabilityViewInfo
